/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { Platform, TouchableOpacity, View } from "react-native";
import Icon from "react-icons-kit";
import { trash } from "react-icons-kit/fa/trash";
import { clone } from "react-icons-kit/fa/clone";
import { circle } from "react-icons-kit/fa/circle";
import { NavLink } from "react-router-dom";

import { Button, Text } from "UpduoLibrary";
import {
  skaleLightGray,
  skaleLinkBlue,
  skaleMainBlue,
  skalePrimaryBlue,
  skaleRed,
  skaleWhite,
  skaleYellow,
  skaleWebBackgroundGray,
  skaleWebTextBackgroundLightGray,
  skaleWebTextGray,
  colorWithOpacity,
} from "config/UpduoColors";
import { getGraphColor } from "config/Utilities";
import { Draggable } from "react-beautiful-dnd";
import * as UpduoAnalytics from "config/UpduoAnalytics";

function SkillDeleteButton({ disabled, onPress }) {
  const [isHoveringDelete, setIsHoveringDelete] = useState(false);

  const buttonColor = isHoveringDelete ? skaleRed : skaleMainBlue;
  return (
    <TouchableOpacity
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringDelete(true);
          },
          onMouseLeave: () => {
            setIsHoveringDelete(false);
          },
        },
      })}
      style={{
        paddingLeft: 10,
        alignItems: "center",
        justifyContent: "center",
      }}
      disabled={disabled}
      onPress={onPress}
    >
      <div
        style={{
          color: disabled ? skaleLightGray : buttonColor,
          paddingBottom: 2.5,
        }}
      >
        <Icon size={15} icon={trash} />
      </div>
    </TouchableOpacity>
  );
}

export default function SkillListing({
  skillObject,
  onPressDeactivate,
  onPressClone,
  disabled,
  skillIndex,
  isDragging,
  canEdit,
}) {
  const [isHoveringDelete, setIsHoveringDelete] = useState(false);
  const [isDeleteConfirmationShown, setIsDeleteConfirmationShown] =
    useState(false);
  const [isHoveringClone, setIsHoveringClone] = useState(false);

  const deleteConfirmation = (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <Text style={{ color: skaleWebTextGray, marginLeft: 20 }}>
        {"Are you sure you want\nto delete this lesson?"}
      </Text>
      <Button
        onPress={() => {
          UpduoAnalytics.logEvent("user-skill-delete-cancel", {
            skillId: skillObject.id,
          });
          setIsDeleteConfirmationShown(false);
        }}
        width={80}
        marginLeft={10}
        text={"Keep"}
        height={28}
        borderWidth={1}
        borderColor={skalePrimaryBlue}
        color={skaleWhite}
        textColor={skalePrimaryBlue}
      />
      <Button
        onPress={() => {
          UpduoAnalytics.logEvent("user-skill-delete-confirm", {
            skillId: skillObject.id,
          });
          onPressDeactivate();
          setIsDeleteConfirmationShown(false);
        }}
        width={120}
        marginLeft={10}
        text={"Delete"}
        height={28}
        borderWidth={1}
        borderColor={skaleRed}
        color={skaleWhite}
        textColor={skaleRed}
      />
    </View>
  );

  const cloneBtnColor = isHoveringClone ? skaleYellow : skaleMainBlue;
  const skillCloneButton = (
    <TouchableOpacity
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringClone(true);
          },
          onMouseLeave: () => {
            setIsHoveringClone(false);
          },
        },
      })}
      onPress={() => {
        UpduoAnalytics.logEvent("user-skill-new", {
          isClone: true,
          cloneId: skillObject.id,
        });
        onPressClone();
      }}
    >
      <div
        style={{
          color: disabled ? skaleLightGray : cloneBtnColor,
          paddingBottom: 2.5,
        }}
      >
        <Icon size={15} icon={clone} />
      </div>
    </TouchableOpacity>
  );

  return (
    <Draggable
      key={skillObject.id}
      draggableId={`skill${skillObject.id}`}
      index={skillIndex}
      type={"skill"}
    >
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <View
              style={[
                {
                  borderWidth: 1,
                  borderRadius: 8,
                  borderColor: skaleWebBackgroundGray,
                  paddingVertical: 5,
                  width: window.innerWidth * 0.6 - 50,
                  minWidth: 400,
                  flexDirection: "row",
                  backgroundColor: isDragging
                    ? "lightblue"
                    : skaleWebTextBackgroundLightGray,
                },
              ]}
            >
              <View
                style={{
                  width: 30,
                  borderColor: skaleWebBackgroundGray,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text>{`${skillObject.order + 1}.`}</Text>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 10,
                  flexShrink: 1,
                }}
              >
                <div
                  style={{
                    color: getGraphColor(skillObject.name),
                    paddingBottom: 2.5,
                  }}
                >
                  <Icon size={15} icon={circle} />
                </div>
                <View style={{ width: 10 }} />
                <NavLink
                  to={`/content/lesson/${skillObject.id}`}
                  style={{
                    textDecoration: "none",
                    color: colorWithOpacity(
                      isHoveringDelete ? skaleLinkBlue : skaleMainBlue,
                      skillObject.isPublished ? 1 : 0.2
                    ),
                  }}
                  onMouseEnter={() => {
                    setIsHoveringDelete(true);
                  }}
                  onMouseLeave={() => {
                    setIsHoveringDelete(false);
                  }}
                  onClick={() => {
                    UpduoAnalytics.logEvent("user-skill", {
                      skillId: skillObject.id,
                    });
                  }}
                >
                  {skillObject.name}
                </NavLink>
              </View>
              <View style={{ flex: 1 }} />
              {canEdit ? (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginRight: 15,
                  }}
                >
                  {skillCloneButton}
                  <SkillDeleteButton
                    disabled={false}
                    onPress={() => {
                      UpduoAnalytics.logEvent("user-skill-delete", {
                        skillId: skillObject.id,
                      });
                      setIsDeleteConfirmationShown(!isDeleteConfirmationShown);
                    }}
                  />
                </View>
              ) : null}
            </View>
            {isDeleteConfirmationShown ? deleteConfirmation : null}
          </View>
        </div>
      )}
    </Draggable>
  );
}
